/** Shared oriented hull panels: rendering, movement, shots and fatal sweeps use
 * these same frames. Round passages are 24-sided tubes with open ends. */
import * as pmath from './pmath.js';
import type {RaceBox,RaceVec3,RaceGate} from './raceCourse.js';

export type RaceBasis = [RaceVec3,RaceVec3,RaceVec3];
export function raceBoxBasis(box:Pick<RaceBox,'yaw'|'basis'>):RaceBasis{
 return box.basis??[[pmath.cos(box.yaw),0,-pmath.sin(box.yaw)],[0,1,0],[pmath.sin(box.yaw),0,pmath.cos(box.yaw)]];
}
export function raceBoxLocal(box:RaceBox,point:readonly number[]):RaceVec3{
 const axes=raceBoxBasis(box),x=point[0]-box.center[0],y=point[1]-box.center[1],z=point[2]-box.center[2];
 return axes.map(a=>a[0]*x+a[1]*y+a[2]*z) as RaceVec3;
}
const extentsCache=new WeakMap<RaceBox,RaceVec3>();
export function raceBoxExtents(box:RaceBox):RaceVec3{
 const cached=extentsCache.get(box);if(cached)return cached;
 const axes=raceBoxBasis(box);
 const extents=[0,1,2].map(i=>axes.reduce((sum,a,j)=>sum+Math.abs(a[i])*box.size[j]/2,0)) as RaceVec3;
 extentsCache.set(box,extents);return extents;
}
export function raceBoxCollider(box:RaceBox,radius:number){
 const axes=raceBoxBasis(box);
 // The movement resolver treats its height axis as the feet/up direction.
 // Permute the same OBB so a pipe wall resolves sideways, never along its rim.
 const heightAxis=box.basis?axes.reduce((best,a,i)=>Math.abs(a[1])>Math.abs(axes[best][1])?i:best,0):1;
 const widthAxis=(heightAxis+2)%3,depthAxis=(heightAxis+1)%3;
 const up=axes[heightAxis].map(v=>v*(axes[heightAxis][1]<0?-1:1)) as RaceVec3;
 return {base:box.center.map((v,i)=>v-up[i]*box.size[heightAxis]/2+(i===1?radius:0)) as RaceVec3,
  up,width:axes[widthAxis],halfWidth:box.size[widthAxis]/2,halfDepth:box.size[depthAxis]/2,
  height:box.size[heightAxis],standable:box.standable,planarSupport:box.planarSupport};
}

const dot=(a:readonly number[],b:readonly number[])=>a[0]*b[0]+a[1]*b[1]+a[2]*b[2];
const normalize=(a:RaceVec3):RaceVec3=>{const n=pmath.hypot(...a);return a.map(v=>v/n) as RaceVec3;};
const cross=(a:RaceVec3,b:RaceVec3):RaceVec3=>[a[1]*b[2]-a[2]*b[1],a[2]*b[0]-a[0]*b[2],a[0]*b[1]-a[1]*b[0]];
export function raceTubeFrame(from:RaceVec3,to:RaceVec3,rightHint:RaceVec3){
 const forward=normalize(to.map((v,i)=>v-from[i]) as RaceVec3);
 const projection=dot(rightHint,forward);
 const right=normalize(rightHint.map((v,i)=>v-forward[i]*projection) as RaceVec3);
 return {forward,right,up:cross(right,forward)};
}
export function makeRaceTubePanels(id:string,kind:'tunnel'|'dive-hazard',path:RaceVec3[],radius:number,rightHint:RaceVec3,
 skip?:(segment:number,radial:RaceVec3)=>boolean):RaceBox[]{
 const panels:RaceBox[]=[],sides=24,thickness=.65;
 const halfAngle=pmath.TWO_PI/(sides*2),width=2*(radius+thickness)*pmath.sin(halfAngle)/pmath.cos(halfAngle);
 for(let segment=0;segment<path.length-1;segment++){
  const a=path[segment],b=path[segment+1],frame=raceTubeFrame(a,b,rightHint);
  const length=pmath.hypot(b[0]-a[0],b[1]-a[1],b[2]-a[2]);
  for(let side=0;side<sides;side++){
   const angle=pmath.TWO_PI*side/sides,c=pmath.cos(angle),s=pmath.sin(angle);
   const radial=frame.right.map((v,i)=>v*c+frame.up[i]*s) as RaceVec3;
   if(skip?.(segment,radial))continue;
   const tangent=frame.right.map((v,i)=>-v*s+frame.up[i]*c) as RaceVec3;
   panels.push({id:`${id}-${segment}-${side}`,kind,center:a.map((v,i)=>(v+b[i])/2+radial[i]*(radius+thickness/2)) as RaceVec3,
    size:[width,thickness,length+.4],basis:[tangent,radial,frame.forward],yaw:pmath.atan2(-frame.forward[0],-frame.forward[2]),standable:false});
  }
 }
 return panels;
}

export function raceGateDistance(gate: RaceGate, point: readonly number[]): number {
  return (point[0] - gate.center[0]) * gate.normal[0] + (point[1] - gate.center[1]) * gate.normal[1] + (point[2] - gate.center[2]) * gate.normal[2];
}

/** Sweep the feet segment so a fast flight frame cannot tunnel past a gate. */
export function crossedRaceGate(gate: RaceGate, from: readonly number[], to: readonly number[]): boolean {
  const before = raceGateDistance(gate, from), after = raceGateDistance(gate, to);
  if (!(before < 0 && after >= 0)) return false;
  const t = -before / (after - before);
  const relative: RaceVec3 = [from[0] + (to[0] - from[0]) * t - gate.center[0], from[1] + (to[1] - from[1]) * t - gate.center[1], from[2] + (to[2] - from[2]) * t - gate.center[2]];
  const lateral = relative[0] * gate.right[0] + relative[1] * gate.right[1] + relative[2] * gate.right[2];
  const up = gate.up ?? [0, 1, 0];
  const vertical = relative[0] * up[0] + relative[1] * up[1] + relative[2] * up[2];
  return gate.shape==='round' ? lateral*lateral/(gate.halfWidth*gate.halfWidth)+vertical*vertical/(gate.halfHeight*gate.halfHeight)<=1
    : Math.abs(lateral) <= gate.halfWidth && Math.abs(vertical) <= gate.halfHeight;
}
